import {
  ATTACHMENT_MAX_COUNT,
  DIAGNOSTICS_B64_MAX_BYTES,
  buildDescription,
  deriveTitle,
  diagnosticsPayload,
  gzipBase64,
} from '@/lib/diagnostics/feedbackSubmission';
import { debugLog } from '@/lib/debugLog';
import { ApiHttpError } from '@/types';
import {
  FEEDBACK_LIST_LIMIT_DEFAULT,
  FEEDBACK_PATCH_WIRE_KEYS,
  type FeedbackAdminItem,
  type FeedbackAdminItemDto,
  type FeedbackAdminListResponseDto,
  type FeedbackAdminPatch,
  type FeedbackAttachmentDto,
  type FeedbackListResponseDto,
  type FeedbackPage,
  type FeedbackReportDetail,
  type FeedbackReportDetailDto,
  type FeedbackReportItem,
  type FeedbackResolutionTag,
  type FeedbackResolutionTagsResponseDto,
  type FeedbackSummary,
  type FeedbackSummaryDto,
} from '@/types/feedback';
import {
  mapFeedbackAdminItemDto,
  mapFeedbackAdminListDto,
  mapFeedbackDetailDto,
  mapFeedbackListDto,
  mapFeedbackSummaryDto,
  mapResolutionTagsResponseDto,
} from '@/types/feedback.mappers';

import type {
  FeedbackAdminListQuery,
  FeedbackListQuery,
  SupportService,
  SupportSubmitInput,
  SupportSubmitResult,
} from '../supportService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`SupportService` — BE `feedback` 契約(docs/94)。
 *
 * ## 為什麼是 multipart(明明 R249/R250 的設計是 JSON + base64)
 *
 * BE 拍板 multipart(docs/94 §2.1「**不要**送 JSON」),FE 照 wire 主導權適配。
 * ⚠️ 但 R251 實測發現 **帶檔案** 的 multipart 會被 Cloud Armor 921150 擋在 GCLB
 * (請求連 BE 都到不了)→ 目前只送**純文字欄位的 multipart**(那條實測通得過),
 * 截圖檔案由 `FEEDBACK_ATTACHMENTS_SUPPORTED` gate。
 *
 * ## 🔴 絕對不要自己設 `Content-Type`
 *
 * multipart 的真值帶 `boundary=----XXXX`,只有瀏覽器在送出當下知道;蓋掉 = 後端解不出欄位。
 * transport 的 FormData 分支**刻意不設**(R245 建立、有負向鎖),這裡靠的就是那條。
 * BE 另外對 multipart **強制要求 `Content-Length`**(chunked 回 411)—— 瀏覽器帶 File 時
 * 自動會算,所以不需要處理,但別把 body 換成 stream。
 */
const PATH = '/api/feedback/reports';
/** BE `feedback/schemas.py` `_VIEWPORT_PATTERN` 的鏡射(不合格 = 整筆 422)。 */
const VIEWPORT_PATTERN = /^\d{2,5}x\d{2,5}$/;
const PATH_SUMMARY = '/api/feedback/reports/summary';
const PATH_TAGS = '/api/feedback/resolution-tags';
const PATH_ADMIN = '/api/feedback/admin/reports';
const PATH_ADMIN_SUMMARY = '/api/feedback/admin/reports/summary';

const detailPath = (reportId: string) => `${PATH}/${encodeURIComponent(reportId)}`;
const adminItemPath = (reportId: string) => `${PATH_ADMIN}/${encodeURIComponent(reportId)}`;

/** 只帶有值的 param(空字串會撞 BE 的 `Literal` / `min_length` 驗證 → 422)。 */
function query(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

/** 404 → 呼叫端自己決定語意(未部署 / 別人的單 / 已刪);其餘錯誤照拋。 */
function isNotFound(err: unknown): boolean {
  return err instanceof ApiHttpError && err.status === 404;
}

interface FeedbackReportDto {
  report_id: string;
  serial: string;
  attachment_count: number;
  attachment_failed_count: number;
  attachments?: FeedbackAttachmentDto[];
}

/** 只 append 有值的欄位:BE 對格式不合的選填欄位是「靜默丟棄」,但空字串會撞必填驗證。 */
function appendIfPresent(form: FormData, key: string, value: string | null | undefined): void {
  if (value !== null && value !== undefined && value !== '') form.append(key, value);
}

export function createHttpSupportService(config: HttpClientConfig): SupportService {
  /** 罐頭標籤目錄快取(見 `listResolutionTags`)。 */
  let tagCache: FeedbackResolutionTag[] | null = null;

  return {
    async submitReport(input: SupportSubmitInput): Promise<SupportSubmitResult> {
      const { report, category, note } = input;
      const form = new FormData();
      form.append('category', category);
      // 兩個必填欄位由 FE 生成 —— owner 要的是「敘述選填」,理由見 feedbackSubmission 檔頭。
      form.append('title', deriveTitle(report, note));
      form.append('description', buildDescription(report, note));

      // BE 建議一律附上的定位線索(docs/94 §2.1)。
      // ⚠️ `page_url` 送**完整** URL 而不是我們遮過 id 的 route:BE 明說這是「後台修 bug 的
      // 定位線索」,而且它只接受自家 origin(其他值靜默丟棄)。診斷碼裡那份仍是遮過的 ——
      // 兩者用途不同,不是矛盾。
      appendIfPresent(form, 'page_url', typeof location === 'undefined' ? '' : location.href);
      appendIfPresent(form, 'app_version', report.client.buildVersion);
      // R413.1 — 鏡射 BE `_VIEWPORT_PATTERN`(`^\d{2,5}x\d{2,5}$`):這欄選填,但格式不對會讓
      // **整筆**回報 422。`window.innerWidth` 在極端情況(背景 / 嵌入)會是 0 → `0x0` 不合格,
      // 寧可不送這欄也要讓回報進得去。
      appendIfPresent(
        form,
        'viewport',
        VIEWPORT_PATTERN.test(report.client.viewport) ? report.client.viewport : null,
      );
      appendIfPresent(form, 'occurred_at', report.createdAt);
      appendIfPresent(form, 'project_id', report.context.projectId);
      // job_id 取第一筆進行中的 job(BE 只收單一值);沒有就不送。
      appendIfPresent(form, 'job_id', report.context.activeJobs[0]?.jobId ?? null);

      // 診斷資料走專用欄位(docs/94 §6)—— gzip + base64。
      //
      // 這三欄是 owner 拍板的 B 案,讓我們原本的資料模型幾乎不用改:整包 `apiTrail` /
      // `consoleTrail` / `annotations` / `client` / `context` 原樣送,後端解開去敏後存起來,
      // admin 端可用 `clientRequestId` 對 Logfire trace。
      //
      // ⚠️ **編碼不是為了省流量**,是讓 Cloud Armor 讀不到內容:診斷全是路徑與 console
      // 原文,`route` 這個 key 直接命中 CRS 的 RCE 規則(932100)—— 那正是 2026-08-19
      // 那次 JSON body 被邊緣 403 擋掉的真因。
      const encoded = await gzipBase64(diagnosticsPayload(report));
      if (encoded.length <= DIAGNOSTICS_B64_MAX_BYTES) {
        form.append('diagnostics_b64', encoded);
      } else {
        // 超過 64 KiB 會讓整筆回報吃 422 —— 文字比診斷重要,寧可不送診斷也要讓回報進得去。
        debugLog('ui', `[report] diagnostics too large (${encoded.length}B) — sending without it`);
      }
      // 我們自己的 `RPT-…` 與後端 serial 並列(客服對話時兩邊都認得)。
      form.append('client_report_id', report.reportId);
      // 路由**模板**(`/projects/{id}/canvas`);真 URL 走 `page_url`,BE 兩欄分開收。
      appendIfPresent(form, 'route', report.context.route);

      // 截圖檔案(WAF 邊緣阻擋已於 2026-08-19 解除 → gate 打開)。
      for (const file of (input.files ?? []).slice(0, ATTACHMENT_MAX_COUNT)) {
        form.append('files', file);
      }

      const res = await httpRequestWithRefresh<FeedbackReportDto>(config, 'POST', PATH, form);
      const dto = res?.result;
      return {
        reportId: dto?.report_id ?? '',
        // serial 是要顯示給使用者的編號;BE 一定會給,拿不到就退回空字串讓 UI 自己決定
        // (⚠️ 不要用 report_id 頂替 —— 那是 ULID,顯示出來只會讓使用者念不出來)。
        serial: dto?.serial ?? '',
        attachmentFailedCount: dto?.attachment_failed_count ?? 0,
      };
    },

    // ── 讀取端(R265;docs/94 §5 / §9)────────────────────────────────────────

    async listMyReports(q: FeedbackListQuery = {}): Promise<FeedbackPage<FeedbackReportItem>> {
      const url =
        PATH +
        query({ status: q.status, cursor: q.cursor, limit: q.limit ?? FEEDBACK_LIST_LIMIT_DEFAULT });
      const env = await httpRequestWithRefresh<FeedbackListResponseDto>(config, 'GET', url);
      // ⚠️ 這裡用 unwrapResult(而不是像 submitReport 那樣容忍空 envelope):對**讀取**
      // 來說「回應沒有 result」與「一筆回報都沒有」在畫面上長得一模一樣 —— 靜默顯示
      // 空狀態會讓使用者以為自己的回報不見了。契約破裂就該吵。
      return mapFeedbackListDto(unwrapResult(env, 'feedback.listMyReports'));
    },

    /** 404 = 別人的單 / 已被 admin 刪掉(BE 刻意不用 403 —— 那等於承認那筆存在)。 */
    async getMyReport(reportId: string): Promise<FeedbackReportDetail | null> {
      try {
        const env = await httpRequestWithRefresh<FeedbackReportDetailDto>(
          config,
          'GET',
          detailPath(reportId),
        );
        return mapFeedbackDetailDto(unwrapResult(env, 'feedback.getMyReport'));
      } catch (err) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    /**
     * ⚠️ **404 → null 而不是 0**:這支是 BE「完成未部署」的一批。回 0 等於宣稱
     * 「你沒有任何回報」,而真相是「我們現在不知道」—— chips 少幾個數字沒關係,
     * 說錯話有關係。
     */
    async getMySummary(): Promise<FeedbackSummary | null> {
      try {
        const env = await httpRequestWithRefresh<FeedbackSummaryDto>(config, 'GET', PATH_SUMMARY);
        return mapFeedbackSummaryDto(unwrapResult(env, 'feedback.getMySummary'));
      } catch (err) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    /**
     * 罐頭標籤目錄。成功後**快取在這個 service 實例上**(session 生命週期)——
     * 它是不會變的文案對照表,而 admin 每開一次處置區就打一次是白花 60/min 配額。
     * ⚠️ 刻意不落 storage:不落地就不必進 `clientStorage` 鑰匙總表、登出不用清。
     */
    async listResolutionTags(): Promise<FeedbackResolutionTag[]> {
      if (tagCache) return tagCache;
      try {
        const env = await httpRequestWithRefresh<FeedbackResolutionTagsResponseDto>(
          config,
          'GET',
          PATH_TAGS,
        );
        const tags = mapResolutionTagsResponseDto(env?.result);
        // 空陣列不進快取:未部署期間回 404 走下面那條,但若 BE 回了 200 + 空清單,
        // 快取住會讓「部署後補上標籤」這件事要重整頁面才看得到。
        if (tags.length > 0) tagCache = tags;
        return tags;
      } catch (err) {
        // 未部署 → 空目錄(admin 處置區顯「標籤目錄尚未部署」,其餘處置照常可用)。
        if (isNotFound(err)) return [];
        throw err;
      }
    },

    async adminListReports(
      q: FeedbackAdminListQuery = {},
    ): Promise<FeedbackPage<FeedbackAdminItem>> {
      const url =
        PATH_ADMIN +
        query({
          status: q.status,
          reporter_uid: q.reporterUid,
          cursor: q.cursor,
          limit: q.limit ?? FEEDBACK_LIST_LIMIT_DEFAULT,
        });
      const env = await httpRequestWithRefresh<FeedbackAdminListResponseDto>(config, 'GET', url);
      return mapFeedbackAdminListDto(unwrapResult(env, 'feedback.adminListReports'));
    },

    async adminGetSummary(): Promise<FeedbackSummary | null> {
      try {
        const env = await httpRequestWithRefresh<FeedbackSummaryDto>(
          config,
          'GET',
          PATH_ADMIN_SUMMARY,
        );
        return mapFeedbackSummaryDto(unwrapResult(env, 'feedback.adminGetSummary'));
      } catch (err) {
        if (isNotFound(err)) return null;
        throw err;
      }
    },

    /**
     * 🔴 **dirty-fields-only**(docs/94 §9):body 只放呼叫端**真的給了**的鍵。
     *
     * `resolutionTags: undefined` → body 沒有 `resolution_tags` = BE 不動這欄;
     * `resolutionTags: []`        → body 帶 `[]`               = BE **清空**已選標籤。
     * 兩者語意不同,所以不可以用 `?? []` 之類的寫法把它們合併(有負向鎖)。
     */
    async adminPatchReport(
      reportId: string,
      patch: FeedbackAdminPatch,
    ): Promise<FeedbackAdminItem | null> {
      const body: Record<string, unknown> = {};
      for (const [field, wireKey] of Object.entries(FEEDBACK_PATCH_WIRE_KEYS) as [
        keyof FeedbackAdminPatch,
        string,
      ][]) {
        const value = patch[field];
        // ⚠️ `undefined` = 沒給 = 不進 body。**不可以**改成 `?? null` 之類的寫法:
        // BE 對「欄位不給」是「不動它」,對 `[]`/`""` 是「清空」。
        if (value !== undefined) body[wireKey] = value;
      }
      if (Object.keys(body).length === 0) {
        // BE 的 422 `FEEDBACK_PATCH_EMPTY` 是兜底;FE 這側直接擋住,不浪費一趟往返
        // 也不讓使用者看到一個看不懂的錯誤。
        throw new ApiHttpError(422, {
          code: 'FEEDBACK_PATCH_EMPTY',
          message: 'feedback.adminPatchReport requires at least one field',
        });
      }
      const env = await httpRequestWithRefresh<FeedbackAdminItemDto>(
        config,
        'PATCH',
        adminItemPath(reportId),
        body,
      );
      // BE 回的是**更新後的完整 admin item**(`admin_detail_of`)—— 直接採用,不必
      // 本地合併、更不必編造 `status_updated_datetime`。
      const dto = env?.result;
      return dto ? mapFeedbackAdminItemDto(dto) : null;
    },

    /**
     * **硬刪** doc + 所有截圖 blob(這條存在的目的就是「請幫我刪掉那張截圖」)。
     * 404 視為成功:第二次刪同一筆 BE 就回 404,而「那筆已經不在了」正是呼叫端要的
     * 結果 —— 對著已達成的狀態吐錯誤只會讓 admin 以為刪失敗而再試一次。
     */
    async adminDeleteReport(reportId: string): Promise<void> {
      try {
        await httpRequestWithRefresh<unknown>(config, 'DELETE', adminItemPath(reportId));
      } catch (err) {
        if (isNotFound(err)) return;
        throw err;
      }
    },
  };
}
